<script setup>
import { h, ref, computed, onMounted, onUnmounted } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import liveReports from 'dashboard/api/liveReports';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud, accountId } = useAccount();
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentRole = useMapGetter('getCurrentRole');
const store = useStore();
const currentAccount = computed(() =>
  store.getters['accounts/getAccount'](accountId.value)
);
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const teamConversationMetric = useMapGetter('getTeamConversationMetric');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const { uiSettings } = useUISettings();
const teamMetricOverrides = ref({});

function extractInboxIdFromFilter(filterPayload) {
  if (!filterPayload?.length) return null;
  const inboxFilter = filterPayload.find(
    f => f.attribute_key === 'inbox_id' && f.filter_operator === 'equal_to'
  );
  return inboxFilter?.values?.[0] || null;
}

async function fetchTeamMetricsWithFilters() {
  const {
    team_default_filter: defaultFilter,
    team_filters_by: teamFilters = {},
  } = uiSettings.value;

  const defaultInboxId = extractInboxIdFromFilter(defaultFilter?.payload);

  // Main call: if there's a default filter, pass inboxId to filter all teams
  store.dispatch('fetchTeamConversationMetric', {
    inboxId: defaultInboxId || undefined,
  });

  // For teams with individual filters (different from default), fetch individually
  const overrides = {};
  const individualFetches = Object.entries(teamFilters)
    .map(([teamId, filter]) => {
      const inboxId = extractInboxIdFromFilter(filter?.payload);
      if (!inboxId || inboxId === defaultInboxId) return null;
      return liveReports
        .getConversationMetric({ team_id: teamId, inbox_id: inboxId })
        .then(res => {
          overrides[Number(teamId)] = res.data?.open ?? 0;
        })
        .catch(() => {});
    })
    .filter(Boolean);

  if (individualFetches.length) {
    await Promise.all(individualFetches);
    teamMetricOverrides.value = overrides;
  } else {
    teamMetricOverrides.value = {};
  }
}

function getTeamCount(teamId) {
  if (teamId in teamMetricOverrides.value) {
    return teamMetricOverrides.value[teamId];
  }
  return (
    teamConversationMetric.value.find(m => m.team_id === teamId)?.open ?? null
  );
}

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  fetchTeamMetricsWithFilters();
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  emitter.on(BUS_EVENTS.TEAM_FILTER_CHANGED, fetchTeamMetricsWithFilters);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.TEAM_FILTER_CHANGED, fetchTeamMetricsWithFilters);
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());
const painelIscasLabel = 'Painel Iscas';

const videoStudioVariant = computed(() => {
  if (
    isFeatureEnabledonAccount.value(accountId.value, 'video_studio_ilimitado')
  ) {
    return 'ilimitado';
  }

  if (isFeatureEnabledonAccount.value(accountId.value, 'video_studio_basic')) {
    return 'basic';
  }

  if (isFeatureEnabledonAccount.value(accountId.value, 'video_studio_trial')) {
    return 'trial';
  }

  if (isFeatureEnabledonAccount.value(accountId.value, 'social_hub')) {
    return 'legacy';
  }

  return 'trial';
});

const videoStudioMenuItem = computed(() => {
  switch (videoStudioVariant.value) {
    case 'ilimitado':
      return {
        name: 'SocialHubVideoIlimitado',
        label: 'Video Studio ILIMITADO',
        icon: 'i-lucide-crown',
        to: accountScopedRoute('social_hub_video_ilimitado'),
        activeOn: ['social_hub_video_ilimitado'],
      };
    case 'basic':
      return {
        name: 'SocialHubVideoBasic',
        label: 'Video Studio BASIC',
        icon: 'i-lucide-video',
        to: accountScopedRoute('social_hub_video_basic'),
        activeOn: ['social_hub_video_basic'],
      };
    case 'legacy':
      return {
        name: 'SocialHubVideoStudio',
        label: t('SIDEBAR.VIDEO_STUDIO'),
        icon: 'i-lucide-clapperboard',
        to: accountScopedRoute('social_hub_video_studio'),
        activeOn: ['social_hub_video_studio'],
      };
    case 'trial':
    default:
      return {
        name: 'SocialHubVideoTrial',
        label: 'Video Studio TRIAL',
        icon: 'i-lucide-flask-conical',
        to: accountScopedRoute('social_hub_video_trial'),
        activeOn: ['social_hub_video_trial'],
      };
  }
});

const menuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        count: 'notifications/getUnreadCount',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
            contactsCount: getTeamCount(team.id),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[12px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Kanban',
      label: t('SIDEBAR.KANBAN'),
      icon: 'i-lucide-layout-panel-left',
      activeOn: ['kanban_view'],
      to: accountScopedRoute('kanban_view'),
    },
    ...(isFeatureEnabledonAccount.value(accountId.value, 'tickets')
      ? [
          {
            name: 'Tickets',
            label: t('SIDEBAR.TICKETS'),
            icon: 'i-lucide-ticket-check',
            activeOn: ['tickets_view'],
            to: accountScopedRoute('tickets_view'),
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(accountId.value, 'commission_management')
      ? [
          {
            name: 'Comissões',
            label: t('COMMISSIONS.TITLE'),
            icon: 'i-lucide-percent',
            activeOn: ['commissions_view'],
            to: accountScopedRoute('commissions_view'),
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(accountId.value, 'products_catalog')
      ? [
          {
            name: 'ProdutosOABPrev',
            label: t('PRODUCTS.TITLE') + ' (OABPrev)',
            icon: 'i-lucide-package',
            activeOn: ['products_view'],
            to: accountScopedRoute('products_view'),
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(accountId.value, 'general_catalog')
      ? [
          {
            name: 'Catalogo',
            label: t('CATALOG.TITLE'),
            icon: 'i-lucide-shopping-bag',
            activeOn: ['catalog_view'],
            to: accountScopedRoute('catalog_view'),
          },
        ]
      : []),
    {
      name: 'Agenda',
      label: t('SIDEBAR.AGENDA'),
      icon: 'i-lucide-calendar-range',
      activeOn: ['agenda_view'],
      to: accountScopedRoute('agenda_view'),
    },
    ...(isFeatureEnabledonAccount.value(accountId.value, 'playbook')
      ? [
          {
            name: 'Playbook',
            label: t('SIDEBAR.PLAYBOOK'),
            icon: 'i-lucide-book-open-text',
            activeOn: ['playbook_view'],
            to: accountScopedRoute('playbook_view'),
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(accountId.value, 'contracts')
      ? [
          {
            name: 'Contracts',
            label: 'Contratos',
            icon: 'i-lucide-file-signature',
            activeOn: ['contracts_index'],
            to: accountScopedRoute('contracts_index'),
          },
        ]
      : []),
    ...(isFeatureEnabledonAccount.value(accountId.value, 'sic_manager')
      ? [
          {
            name: 'SIC Manager',
            label: 'SIC Manager',
            icon: 'i-lucide-rocket',
            activeOn: ['sic_manager', 'sic_manager_project'],
            to: accountScopedRoute('sic_manager'),
          },
          {
            name: 'AgentOps Health',
            label: 'AgentOps Health',
            icon: 'i-lucide-activity',
            activeOn: ['agent_health'],
            to: accountScopedRoute('agent_health'),
          },
        ]
      : []),
    {
      name: 'IA Mestre',
      label: 'IA Mestre',
      icon: 'i-lucide-brain-circuit',
      activeOn: ['master_ai_view'],
      to: accountScopedRoute('master_ai_view'),
    },
    {
      name: 'SocialHub',
      label: t('SIDEBAR.SOCIAL_HUB'),
      icon: 'i-lucide-film',
      activeOn: [
        'social_hub_video_studio',
        'social_hub_instagram_automations',
        'social_hub_video_trial',
        'social_hub_video_basic',
        'social_hub_video_ilimitado',
      ],
      children: [
        videoStudioMenuItem.value,
        {
          name: 'SocialHubInstagramAutomations',
          label: t('SIDEBAR.INSTAGRAM_AUTOMATIONS'),
          icon: 'i-lucide-zap',
          to: accountScopedRoute('social_hub_instagram_automations'),
          activeOn: ['social_hub_instagram_automations'],
        },
      ],
    },
    {
      name: 'Claw Agents',
      label: 'Claw Agents',
      icon: 'i-lucide-brain-circuit',
      activeOn: ['claw_agents_view'],
      to: accountScopedRoute('claw_agents_view'),
    },
    {
      name: 'IA Comercial',
      label: 'IA Comercial',
      icon: 'i-lucide-bot-message-square',
      activeOn: ['ia_comercial_view', 'ia_comercial_settings'],
      to: accountScopedRoute('ia_comercial_view'),
    },
    {
      name: 'IA Super Funcionários',
      label: 'IA Super Funcionários',
      icon: 'i-lucide-sparkles',
      activeOn: ['super_funcionarios_view'],
      to: accountScopedRoute('super_funcionarios_view'),
    },
    {
      name: 'IA Entrevista',
      label: 'IA Entrevista',
      icon: 'i-lucide-clipboard-list',
      activeOn: ['ia_entrevista_view'],
      to: accountScopedRoute('ia_entrevista_view'),
    },
    {
      name: 'Conversões',
      label: 'Conversões',
      icon: 'i-lucide-trending-up',
      activeOn: ['conversions_view'],
      to: accountScopedRoute('conversions_view'),
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            contactsCount: label.contacts_count || 0,
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
        {
          name: 'IA Overview',
          label: 'IA & Transbordos',
          to: accountScopedRoute('ia_overview_reports'),
        },
        {
          name: 'IA Handoffs',
          label: 'Registro de Transbordos',
          to: accountScopedRoute('ia_handoffs_reports'),
        },
        {
          name: 'IA Analyses',
          label: 'Análise de Conversas',
          to: accountScopedRoute('ia_analyses_reports'),
        },
        {
          name: 'Performance Reports',
          label: 'Performance Comercial',
          to: accountScopedRoute('performance_reports'),
        },
        {
          name: 'Proposals Reports',
          label: 'Propostas de Melhoria',
          to: accountScopedRoute('proposals_reports'),
        },
        {
          name: 'Resolution Reports',
          label: t('RESOLUTION_REPORTS.HEADER'),
          to: accountScopedRoute('resolution_reports'),
        },
        ...(isFeatureEnabledonAccount.value(accountId.value, 'ai_dashboard')
          ? [
              {
                name: 'AI Dashboard',
                label: 'Dashboard IA',
                to: accountScopedRoute('ai_dashboard_reports'),
              },
            ]
          : []),
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'WhatsApp broadcasts',
          label: t('SIDEBAR.WHATSAPP_BROADCASTS'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
        {
          name: 'WhatsApp new campaign',
          label: t('SIDEBAR.NEW_WHATSAPP_BROADCAST'),
          to: accountScopedRoute('campaigns_whatsapp_new'),
        },
      ],
    },
    {
      name: 'WhatsApp Instances',
      label: t('SIDEBAR.WHATSAPP_INSTANCES'),
      icon: 'i-lucide-smartphone',
      to: accountScopedRoute('settings_inbox_instances'),
      activeOn: ['settings_inbox_instances'],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
        // {
        //   name: 'Settings Captain',
        //   label: t('SIDEBAR.CAPTAIN_AI'),
        //   icon: 'i-woot-captain',
        //   to: accountScopedRoute('captain_settings_index'),
        // },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          to: accountScopedRoute('assignment_policy_index'),
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings WhatsApp Instances',
          label: t('SIDEBAR.WHATSAPP_INSTANCES'),
          icon: 'i-lucide-smartphone',
          to: accountScopedRoute('settings_inbox_instances'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Agent Bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('agent_bots'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
        },
        {
          name: 'Conversation Workflow',
          label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('conversation_workflow_index'),
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
      ],
    },
  ];
});

const filteredMenuItems = computed(() => {
  if (currentRole.value === 'administrator') return menuItems.value;
  const config = currentAccount.value?.sidebar_config || {};
  const hiddenRoutes = config.agent_hidden_routes || [];
  const allowedChildren = config.agent_allowed_children || [];
  if (!hiddenRoutes.length) return menuItems.value;
  return menuItems.value
    .map(item => {
      if (hiddenRoutes.includes(item.name) && item.children?.length && allowedChildren.length) {
        const kept = item.children.filter(c => allowedChildren.includes(c.name));
        if (kept.length) return { ...item, children: kept };
        return null;
      }
      if (hiddenRoutes.includes(item.name)) return null;
      return item;
    })
    .filter(Boolean);
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-background flex flex-col text-sm pb-0.5 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 ltr:border-r rtl:border-l border-n-weak"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section
      class="grid"
      :class="isEffectivelyCollapsed ? 'mt-3 mb-6 gap-4' : 'mt-1 mb-4 gap-2'"
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="flex-shrink-0 flex items-center pl-3">
            <Logo class="h-10 w-auto" />
          </div>
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-all duration-100 ease-out hover:bg-n-alpha-2 dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle, isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in filteredMenuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
      <!-- Painel Iscas — link externo para super_admin -->
      <a
        v-if="!isEffectivelyCollapsed"
        href="/video-studio/painel"
        target="_blank"
        class="flex items-center gap-2 px-2 py-1.5 mx-1 rounded-lg text-n-slate-11 hover:bg-n-slate-3/70 hover:text-n-slate-12 transition-colors text-sm font-medium mt-1"
      >
        <span class="i-lucide-layout-dashboard size-4 shrink-0" />
        <span class="truncate">{{ painelIscasLabel }}</span>
        <span
          class="i-lucide-external-link size-3 shrink-0 ml-auto opacity-50"
        />
      </a>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="p-1 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
